/* Atelier motion — Chef Nouel Catis
   Everything here is progressive: content is fully visible without it. */
(() => {
  const root = document.documentElement;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const motion = root.classList.contains('atl-motion');
  const desktop = () => window.matchMedia('(min-width: 990px)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- Menu overlay: image swap (works without GSAP) ---------- */
  const initMenu = () => {
    const menu = $('#MenuOverlay');
    if (!menu) return;
    const imgs = $$('[data-menu-image]', menu);
    const show = (i) => {
      if (!imgs.length) return;
      const target = imgs[i % imgs.length];
      imgs.forEach((el) => el.classList.toggle('is-active', el === target));
    };
    $$('[data-menu-index]', menu).forEach((a) => {
      const i = Number(a.dataset.menuIndex);
      a.addEventListener('mouseenter', () => show(i));
      a.addEventListener('focus', () => show(i));
    });
    // Closing the menu when a link is followed keeps the curtain sequence clean
    menu.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (a && a.getAttribute('href').startsWith('#')) menu.querySelector('[data-drawer-close]')?.click();
    });
  };

  /* ---------- Page transitions ---------- */
  const initTransitions = () => {
    if (!root.classList.contains('atl-transitions')) return;
    document.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest('a[href]');
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      if (a.closest('[data-no-transition]') || a.matches('[data-drawer-open],[data-search-open],[data-no-transition]')) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#') || /^(mailto|tel|sms|javascript):/i.test(href)) return;
      let url;
      try { url = new URL(a.href, location.href); } catch (_) { return; }
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
      e.preventDefault();
      try { sessionStorage.setItem('atl-nav', '1'); } catch (_) {}
      root.classList.remove('atl-arrive');
      root.classList.add('atl-leave');
      setTimeout(() => { location.href = url.href; }, 560);
    });
    // Back/forward cache: never restore a closed curtain
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) root.classList.remove('atl-leave', 'atl-arrive');
    });
  };

  /* ---------- Footer wordmark letters ---------- */
  const splitLetters = (el) => {
    const text = el.textContent.trim();
    el.textContent = '';
    [...text].forEach((ch) => {
      const s = document.createElement('span');
      s.className = 'atl-l';
      s.textContent = ch === ' ' ? ' ' : ch;
      el.appendChild(s);
    });
    return $$('.atl-l', el);
  };

  /* ---------- Word split (keeps inline markup such as <em>) ---------- */
  const splitWords = (el) => {
    if (el.dataset.splitDone) return $$('.atl-w__i', el);
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((p) => {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(' ')); return; }
            const w = document.createElement('span');
            w.className = 'atl-w';
            const i = document.createElement('span');
            i.className = 'atl-w__i';
            i.textContent = p;
            w.appendChild(i);
            frag.appendChild(w);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(el);
    el.dataset.splitDone = '1';
    return $$('.atl-w__i', el);
  };

  /* ---------- Count-up ---------- */
  const countUp = (el, gsap) => {
    const raw = el.dataset.count || el.textContent;
    const m = raw.match(/^(\D*)([\d,.]+)(.*)$/);
    if (!m) return;
    const end = parseFloat(m[2].replace(/,/g, ''));
    if (!isFinite(end)) return;
    const isYear = end > 1900 && end < 2100;
    const from = isYear ? end - 40 : 0;
    const obj = { v: from };
    const ST = window.ScrollTrigger;
    if (!ST) return;  // no animation: the real figure is already on the page
    ST.create({
      trigger: el, start: 'top 88%', once: true,
      onEnter: () => {
        el.textContent = m[1] + from + m[3];
        gsap.to(obj, { v: end, duration: isYear ? 1.8 : 1.4, ease: 'power3.out',
          onUpdate: () => { el.textContent = m[1] + Math.round(obj.v) + m[3]; },
          onComplete: () => { el.textContent = raw; } });
      }
    });
  };

  /* ---------- Index: floating image follows the cursor ---------- */
  const initIndexFloat = () => {
    $$('[data-index]').forEach((section) => {
      const float = $('[data-index-float]', section);
      const rows = $$('[data-index-row]', section);
      if (!float || !rows.length || !finePointer) return;
      // Clone each row's thumbnail into the floating frame
      rows.forEach((row, i) => {
        const src = $('[data-index-image] img, [data-index-image] svg', row);
        if (!src) return;
        const c = src.cloneNode(true);
        c.removeAttribute('loading');
        c.style.opacity = i === 0 ? '1' : '0';
        c.style.transition = 'opacity .45s ease, transform 1s cubic-bezier(.16,1,.3,1)';
        c.dataset.floatImg = i;
        float.appendChild(c);
      });
      const imgs = $$('[data-float-img]', float);
      let x = innerWidth / 2, y = innerHeight / 2, cx = x, cy = y, raf = null, active = false;
      const loop = () => {
        cx += (x - cx) * 0.14;
        cy += (y - cy) * 0.14;
        float.style.setProperty('--fx', `${cx}px`);
        float.style.setProperty('--fy', `${cy}px`);
        raf = active || Math.abs(x - cx) + Math.abs(y - cy) > 0.5 ? requestAnimationFrame(loop) : null;
      };
      const kick = () => { if (!raf) raf = requestAnimationFrame(loop); };
      const list = $('.atl-index__list', section);
      list.addEventListener('mousemove', (e) => { x = e.clientX; y = e.clientY; kick(); });
      list.addEventListener('mouseenter', (e) => {
        if (!desktop()) return;
        cx = x = e.clientX; cy = y = e.clientY;
        active = true; float.classList.add('is-visible'); kick();
      });
      list.addEventListener('mouseleave', () => { active = false; float.classList.remove('is-visible'); });
      rows.forEach((row, i) => {
        row.addEventListener('mouseenter', () => {
          imgs.forEach((im) => { const on = Number(im.dataset.floatImg) === i; im.style.opacity = on ? '1' : '0'; im.style.transform = on ? 'scale(1)' : 'scale(1.08)'; });
        });
      });
    });
  };

  /* ---------- Journey progress (native scroll fallback / mobile) ---------- */
  const bindTrackProgress = (section) => {
    const track = $('[data-journey-track]', section);
    const bar = $('[data-journey-progress]', section);
    if (!track || !bar) return;
    const set = () => {
      const max = track.scrollWidth - track.clientWidth;
      bar.style.width = max > 0 ? `calc((100% - 2 * var(--gx)) * ${track.scrollLeft / max})` : '0';
    };
    track.addEventListener('scroll', set, { passive: true });
    set();
  };

  /* ======================================================================
     GSAP layer
     ====================================================================== */
  const initGsap = () => {
    const gsap = window.gsap;
    const ST = window.ScrollTrigger;
    if (!gsap || !ST) {
      $$('[data-journey]').forEach(bindTrackProgress);
      return;
    }
    gsap.registerPlugin(ST);
    const ease = 'expo.out';

    /* Smooth scroll */
    let lenis = null;
    if (document.body.dataset.smooth === 'true' && window.Lenis && finePointer) {
      lenis = new window.Lenis({ duration: 1.15, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true });
      lenis.on('scroll', ST.update);
      gsap.ticker.add((t) => lenis.raf(t * 1000));
      gsap.ticker.lagSmoothing(0);
      new MutationObserver(() => {
        document.body.classList.contains('is-locked') ? lenis.stop() : lenis.start();
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      document.addEventListener('click', (e) => {
        const a = e.target.closest('a[href^="#"]');
        if (!a || a.getAttribute('href').length < 2) return;
        const t = document.querySelector(a.getAttribute('href'));
        if (t) { e.preventDefault(); lenis.scrollTo(t, { offset: -80 }); }
      });
      window.atlLenis = lenis;
    }

    const introDelay = root.classList.contains('atl-intro-play') ? 2.4 : root.classList.contains('atl-arrive') ? 0.55 : 0.1;

    /* Hero */
    $$('[data-atl-hero]').forEach((hero) => {
      const media = $('.atl-hero__media img, .atl-hero__media video', hero);
      const title = $('[data-split]', hero);
      const words = title ? splitWords(title) : [];
      const fades = $$('[data-fade]', hero);
      const tl = gsap.timeline({ delay: introDelay });
      if (media) tl.fromTo(media, { scale: 1.22 }, { scale: 1.12, duration: 2.6, ease: 'power3.out' }, 0);
      if (title) tl.set(title, { opacity: 1 }, 0);
      if (words.length) tl.from(words, { yPercent: 115, rotate: 4, duration: 1.4, ease, stagger: 0.07 }, 0.15);
      if (fades.length) tl.fromTo(fades, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 1.1, ease, stagger: 0.08 }, 0.55);
      if (media) {
        gsap.to(media, { yPercent: 12, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
      }
      const content = $('.atl-hero__content', hero);
      if (content) gsap.to(content, { opacity: 0, y: -60, ease: 'none', scrollTrigger: { trigger: hero, start: '35% top', end: 'bottom top', scrub: true } });
    });
    root.classList.add('atl-ready');

    // Pinned sections are created first so every later trigger measures past their scroll distance
    /* Journey: pinned horizontal travel on desktop */
    const mm = gsap.matchMedia();
    $$('[data-journey]').forEach((sec) => {
      const track = $('[data-journey-track]', sec);
      const pin = $('.atl-journey__pin', sec);
      const bar = $('[data-journey-progress]', sec);
      if (!track || !pin) return;
      mm.add('(min-width: 990px)', () => {
        sec.classList.add('is-pinned');
        const dist = () => Math.max(0, track.scrollWidth - window.innerWidth);
        const tween = gsap.to(track, {
          x: () => -dist(), ease: 'none',
          scrollTrigger: {
            trigger: pin, pin: true, start: 'top top', end: () => `+=${dist()}`, scrub: 0.9, invalidateOnRefresh: true, anticipatePin: 1,
            onUpdate: (self) => { if (bar) bar.style.width = `calc((100% - 2 * var(--gx)) * ${self.progress})`; }
          }
        });
        // Ghost city names drift against the cards
        $$('.atl-city__ghost', sec).forEach((g) => {
          gsap.fromTo(g, { xPercent: 18 }, { xPercent: -18, ease: 'none', scrollTrigger: { trigger: g.parentNode, containerAnimation: tween, start: 'left right', end: 'right left', scrub: true } });
        });
        $$('.atl-city__frame img', sec).forEach((im) => {
          gsap.fromTo(im, { scale: 1.18 }, { scale: 1, ease: 'none', scrollTrigger: { trigger: im.closest('.atl-city'), containerAnimation: tween, start: 'left right', end: 'center center', scrub: true } });
        });
        return () => { sec.classList.remove('is-pinned'); gsap.set(track, { clearProps: 'transform' }); };
      });
      mm.add('(max-width: 989px)', () => { bindTrackProgress(sec); });
    });


    /* Split headings (outside hero) */
    $$('[data-split]').forEach((el) => {
      if (el.closest('[data-atl-hero]')) return;
      const words = splitWords(el);
      gsap.from(words, { yPercent: 115, duration: 1.25, ease, stagger: 0.05, scrollTrigger: { trigger: el, start: 'top 86%', once: true } });
    });

    /* Generic fades, batched for siblings */
    const fades = $$('[data-fade]').filter((el) => !el.closest('[data-atl-hero]'));
    gsap.set(fades, { opacity: 0, y: 36 });
    ST.batch(fades, {
      start: 'top 90%', once: true,
      onEnter: (batch) => gsap.to(batch, { opacity: 1, y: 0, duration: 1.15, ease, stagger: 0.09, overwrite: true })
    });

    /* Manifesto: words brighten as you read */
    $$('[data-words]').forEach((el) => {
      const words = splitWords(el);
      words.forEach((w) => w.parentNode.classList.add('atl-mw'));
      gsap.fromTo(words, { opacity: 0.16 }, {
        opacity: 1, ease: 'none', stagger: 0.1,
        scrollTrigger: { trigger: el, start: 'top 80%', end: 'bottom 45%', scrub: 0.6 }
      });
    });

    /* Parallax helpers */
    $$('[data-parallax-inner]').forEach((el) => {
      gsap.fromTo(el, { yPercent: -7 }, { yPercent: 7, ease: 'none', scrollTrigger: { trigger: el.parentNode, start: 'top bottom', end: 'bottom top', scrub: true } });
    });
    $$('[data-parallax]').forEach((el) => {
      if (el.closest('[data-atl-hero]')) return;
      const amt = Number(el.dataset.parallax) || 10;
      gsap.fromTo(el, { yPercent: -amt / 2 }, { yPercent: amt / 2, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true } });
    });

    /* Gilt line drawing for ornaments */
    $$('[data-draw]').forEach((svg) => {
      $$('rect, circle, path', svg).forEach((el) => {
        const fill = el.getAttribute('fill');
        if (fill && fill !== 'none') return;
        const len = el.getTotalLength ? el.getTotalLength() : 0;
        if (!len) return;
        gsap.fromTo(el, { strokeDasharray: len, strokeDashoffset: len }, {
          strokeDashoffset: 0, duration: 2.4, ease: 'power2.inOut',
          scrollTrigger: { trigger: svg, start: 'top 88%', once: true }
        });
      });
    });

    /* Counters */
    $$('[data-count]').forEach((el) => countUp(el, gsap));

    /* Image reveal: framed window opens to full bleed */
    $$('[data-reveal-image]').forEach((sec) => {
      const frame = $('[data-reveal-frame]', sec);
      const cap = $('[data-reveal-caption]', sec);
      const img = $('img, svg', frame);
      const inset = desktop() ? '18% 30% 18% 30%' : '22% 12% 22% 12%';
      const tl = gsap.timeline({ scrollTrigger: { trigger: sec, start: 'top top', end: 'bottom bottom', scrub: 0.8 } });
      tl.fromTo(frame, { clipPath: `inset(${inset} round 240px 240px 0 0)` }, { clipPath: 'inset(0% 0% 0% 0% round 0px 0px 0 0)', ease: 'power2.inOut', duration: 1 }, 0);
      if (img) tl.fromTo(img, { scale: 1.35 }, { scale: 1, ease: 'power2.inOut', duration: 1 }, 0);
      if (cap) tl.fromTo(cap, { opacity: 0, y: 50 }, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.35 }, 0.62);
    });

    /* Marquee leans with scroll velocity */
    $$('.atl-marquee__track').forEach((track) => {
      const skew = gsap.quickTo(track, 'skewX', { duration: 0.5, ease: 'power3' });
      ST.create({ trigger: track, start: 'top bottom', end: 'bottom top', onUpdate: (s) => skew(gsap.utils.clamp(-6, 6, s.getVelocity() / -400)) });
    });

    /* Footer wordmark rises letter by letter */
    $$('[data-wordmark]').forEach((el) => {
      const letters = splitLetters(el);
      gsap.from(letters, { yPercent: 100, opacity: 0, duration: 1.3, ease, stagger: 0.04, scrollTrigger: { trigger: el, start: 'top 95%', once: true } });
    });

    /* Header: lift border/shadow handled by theme.js; refresh once fonts/images settle */
    ST.sort();
    window.addEventListener('load', () => ST.refresh());
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => ST.refresh());
  };

  /* ---------- Layout helpers: announcement height, hero state ---------- */
  const initLayout = () => {
    const bar = $('.announcement');
    const setH = () => root.style.setProperty('--announce-h', `${bar ? bar.offsetHeight : 0}px`);
    setH();
    window.addEventListener('resize', setH, { passive: true });
    const hero = $('[data-atl-hero]');
    if (hero && 'IntersectionObserver' in window) {
      document.body.classList.add('atl-on-hero');
      new IntersectionObserver(([e]) => document.body.classList.toggle('atl-on-hero', e.intersectionRatio > 0.35), { threshold: [0, 0.35, 1] }).observe(hero);
    }
  };

  const boot = () => {
    initLayout();
    initMenu();
    initTransitions();
    initIndexFloat();
    if (motion) {
      initGsap();
    } else {
      $$('[data-journey]').forEach(bindTrackProgress);
      root.classList.add('atl-ready');
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
